'use client'

import clsx from 'clsx'
import { ReactNode, useEffect, useRef, useState } from 'react'
import SwiperCore from 'swiper'
import 'swiper/css'
import 'swiper/css/a11y'
import 'swiper/css/free-mode'
import { A11y, FreeMode, Mousewheel } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import cl from './elements-slider-free.module.scss'

SwiperCore.use([Mousewheel])

interface ElementsSliderFreeProps {
	children?: ReactNode[]
	gap?: number
	classNameRoot?: string
	classNameSwiper?: string
	classNameSlide?: string
	keyExctractor?: (item: ReactNode, index: number) => string | number
}

export const ElementsSliderFree = (props: ElementsSliderFreeProps) => {
	const { children = [], gap = 0, classNameRoot, classNameSwiper, classNameSlide, keyExctractor } = props

	const rootRef = useRef<HTMLDivElement>(null)
	// Swiper init (updateSize) forces synchronous layout — a big source of render-delay/INP when many
	// below-the-fold carousels hydrate at once. Defer the heavy Swiper until the carousel is near the
	// viewport; until then render a native horizontal-scroll fallback so content stays in SSR and usable.
	const [enhanced, setEnhanced] = useState(false)

	useEffect(() => {
		if (enhanced) return

		const el = rootRef.current
		if (!el || typeof IntersectionObserver === 'undefined') {
			setEnhanced(true)
			return
		}

		const observer = new IntersectionObserver(
			entries => {
				if (entries.some(entry => entry.isIntersecting)) {
					setEnhanced(true)
					observer.disconnect()
				}
			},
			{ rootMargin: '200px' }
		)
		observer.observe(el)

		return () => observer.disconnect()
	}, [enhanced])

	const getKey = (item: ReactNode, index: number) => (keyExctractor ? keyExctractor(item, index) : index)

	if (!enhanced) {
		return (
			<div ref={rootRef} className={clsx(cl.root, classNameRoot)}>
				<div className={clsx(cl.fallback, classNameSwiper)} style={{ gap }}>
					{children.map((item, i) => (
						<div key={getKey(item, i)} className={clsx(cl.slide, classNameSlide)}>
							{item}
						</div>
					))}
				</div>
			</div>
		)
	}

	return (
		<div ref={rootRef} className={clsx(cl.root, classNameRoot)}>
			<Swiper
				slidesPerView="auto"
				mousewheel={{
					forceToAxis: true,
					releaseOnEdges: true
				}}
				spaceBetween={gap}
				freeMode={true}
				modules={[FreeMode, A11y]}
				className={clsx(cl.swiper, classNameSwiper)}>
				{children.map((item, i) => (
					<SwiperSlide key={getKey(item, i)} className={clsx(cl.slide, classNameSlide)}>
						{item}
					</SwiperSlide>
				))}
			</Swiper>
		</div>
	)
}
